import Vue from "vue";
import ApiService from "../../services/ApiService";
import { EventBus, EVENTS } from "../../services/EventBus";

Vue.component("start-bench-sell-widget", {
  delimiters: ["${", "}"],

  props: {
    player1: {
      type: Object,
      required: true,
    },
    player2: {
      type: Object,
      required: true,
    },
    player3: {
      type: Object,
      required: true,
    },
    startText: {
      type: String,
      default: "Start",
    },
    benchText: {
      type: String,
      default: "Bench",
    },
    sellText: {
      type: String,
      default: "Sell",
    },
  },

  data() {
    return {
      votes: {
        player1: { start: 0, bench: 0, sell: 0 },
        player2: { start: 0, bench: 0, sell: 0 },
        player3: { start: 0, bench: 0, sell: 0 },
      },
      totalVotes: {
        start: 0,
        bench: 0,
        sell: 0,
      },
      userVotes: {
        start: null, // "player1", "player2", "player3" oder null
        bench: null,
        sell: null,
      },
      selectedVotes: {
        start: null, // Temporäre Auswahl vor Submit
        bench: null,
        sell: null,
      },
      hasSubmittedVote: false, // Hat der User bereits abgestimmt?
      isLoading: false,
      message: "",
      isLoggedIn: false,
      technicalDataCollapsed: {
        player1: true,
        player2: true,
        player3: true,
      },
    };
  },

  computed: {
    canSubmit() {
      // User kann abstimmen wenn alle 3 Aktionen ausgewählt wurden
      return this.selectedVotes.start && this.selectedVotes.bench && this.selectedVotes.sell;
    },
    player1Name() {
      return this.player1?.texts?.[0]?.name1 || "Spieler 1";
    },
    player2Name() {
      return this.player2?.texts?.[0]?.name1 || "Spieler 2";
    },
    player3Name() {
      return this.player3?.texts?.[0]?.name1 || "Spieler 3";
    },
    player1Position() {
      return this.getPropertyValue(this.player1, 73) || "";
    },
    player2Position() {
      return this.getPropertyValue(this.player2, 73) || "";
    },
    player3Position() {
      return this.getPropertyValue(this.player3, 73) || "";
    },
    player1MarketValue() {
      const value = this.getPropertyValue(this.player1, 58);
      return value ? Number(value).toLocaleString("de-DE") + "€" : "";
    },
    player2MarketValue() {
      const value = this.getPropertyValue(this.player2, 58);
      return value ? Number(value).toLocaleString("de-DE") + "€" : "";
    },
    player3MarketValue() {
      const value = this.getPropertyValue(this.player3, 58);
      return value ? Number(value).toLocaleString("de-DE") + "€" : "";
    },
    player1Url() {
      return this.getPlayerUrl(this.player1);
    },
    player2Url() {
      return this.getPlayerUrl(this.player2);
    },
    player3Url() {
      return this.getPlayerUrl(this.player3);
    },
    player1TechnicalData() {
      return this.getTechnicalData(this.player1);
    },
    player2TechnicalData() {
      return this.getTechnicalData(this.player2);
    },
    player3TechnicalData() {
      return this.getTechnicalData(this.player3);
    },
  },

  mounted() {
    if (typeof window !== "undefined") {
      this.loadVotes();
      this.checkLoginStatus();
    }
  },

  methods: {
    loadVotes() {
      const params = {
        player1Id: this.player1.item?.id,
        player2Id: this.player2.item?.id,
        player3Id: this.player3.item?.id,
      };

      ApiService.get("/rest/fanliebling/start-bench-sell/votes", params)
        .done((response) => {
          this.votes = response.votes || {
            player1: { start: 0, bench: 0, sell: 0 },
            player2: { start: 0, bench: 0, sell: 0 },
            player3: { start: 0, bench: 0, sell: 0 },
          };
          this.totalVotes = response.totalVotes || { start: 0, bench: 0, sell: 0 };
          this.userVotes = response.userVotes || { start: null, bench: null, sell: null };
          this.hasSubmittedVote = !!(this.userVotes.start || this.userVotes.bench || this.userVotes.sell);
          
          // Setze selectedVotes auf userVotes wenn User bereits abgestimmt hat
          if (this.hasSubmittedVote) {
            this.selectedVotes = { ...this.userVotes };
          }
          
          this.isLoggedIn = response.isLoggedIn === true || 
                           (response.userVotes && (response.userVotes.start || response.userVotes.bench || response.userVotes.sell));
        })
        .fail((error) => {
          this.checkLoginStatus();
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    checkLoginStatus() {
      if (this.$store && this.$store.state && this.$store.state.user) {
        this.isLoggedIn = !!this.$store.state.user.isLoggedIn;
      }
    },

    selectVote(player, action) {
      // Einfache Auswahl ohne Submit
      if (!this.isLoggedIn) {
        this.openLoginModal();
        return;
      }

      // Wenn bereits abgestimmt, nichts tun (muss erst "Stimmen ändern" klicken)
      if (this.hasSubmittedVote) {
        return;
      }

      // Toggle: Wenn gleicher Player nochmal geklickt, deselektieren
      if (this.selectedVotes[action] === player) {
        this.$set(this.selectedVotes, action, null);
      } else {
        this.$set(this.selectedVotes, action, player);
      }
    },

    submitVotes() {
      if (!this.canSubmit || this.isLoading || !this.isLoggedIn) {
        return;
      }

      this.isLoading = true;
      this.message = "";

      // Sende alle 3 Votes in einem Request
      const payload = {
        player1Id: this.player1.item?.id,
        player2Id: this.player2.item?.id,
        player3Id: this.player3.item?.id,
        votes: this.selectedVotes, // { start: "player1", bench: "player2", sell: "player3" }
      };

      ApiService.post("/rest/fanliebling/start-bench-sell/vote", payload)
        .done((response) => {
          const data = response.data || response;

          if (data && data.votes) {
            this.$set(this, "votes", data.votes);
            this.$set(this, "totalVotes", data.totalVotes || { start: 0, bench: 0, sell: 0 });
            this.$set(this, "userVotes", { ...this.selectedVotes });
            this.hasSubmittedVote = true;

            this.message = "Vielen Dank für deine Stimme!";

            // Event auslösen für Badge-Update
            EventBus.$emit(EVENTS.INTERACTION_COMPLETED);

            setTimeout(() => {
              this.message = "";
            }, 3000);
          } else {
            this.message = "Ein Fehler ist aufgetreten.";
          }
        })
        .fail((error) => {
          this.message = "Ein Fehler ist aufgetreten.";
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    changeVotes() {
      // Stimmen zurücknehmen und neu wählen lassen
      this.selectedVotes = { start: null, bench: null, sell: null };
      this.hasSubmittedVote = false;
      this.message = "";
    },

    isButtonDisabled(player, action) {
      // Button ist disabled wenn:
      // 1. User ist nicht eingeloggt
      // 2. User hat bereits abgestimmt (muss erst "Stimmen ändern" klicken)
      if (!this.isLoggedIn || this.hasSubmittedVote) {
        return true;
      }

      return false;
    },

    getButtonClass(player, action) {
      const isSelected = this.selectedVotes[action] === player;
      
      // SSR-safe: Default to desktop size on server, check window only on client
      const width = typeof window !== "undefined" ? window.innerWidth : 1024;
      let classes = "btn ";
      
      // Mobile (< 768px): btn-md
      // Tablet (768px - 1023px): btn-sm
      // Desktop (>= 1024px): btn-md
      if (width < 768) {
        classes += "btn-md";
      } else if (width >= 768 && width < 1024) {
        classes += "btn-sm";
      } else {
        classes += "btn-md";
      }
      
      if (isSelected) {
        if (action === "start") {
          classes += " btn-success";
        } else if (action === "bench") {
          classes += " btn-warning";
        } else if (action === "sell") {
          classes += " btn-danger";
        }
      } else {
        if (action === "start") {
          classes += " btn-outline-success";
        } else if (action === "bench") {
          classes += " btn-outline-warning";
        } else if (action === "sell") {
          classes += " btn-outline-danger";
        }
      }
      
      return classes;
    },

    openLoginModal() {
      // SSR-safe: Only run on client
      if (typeof document === "undefined") {
        return;
      }
      const loginButton = document.querySelector('.control-user a[data-toggle="modal"]');
      if (loginButton) {
        loginButton.click();
      }
    },

    getPlayerUrl(player) {
      if (!player) return "#";
      
      const itemId = player.item?.id || 0;
      const variationId = player.variation?.id || player.ids?.variation || player.id || 0;
      
      if (player.texts?.[0]?.urlPath && itemId > 0 && variationId > 0) {
        return "/" + player.texts[0].urlPath + "_" + itemId + "_" + variationId;
      }
      
      if (variationId > 0 && itemId > 0) {
        const itemName = player.texts?.[0]?.name1 || "";
        
        if (itemName) {
          const slug = itemName.toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
          return `/${slug}_${itemId}_${variationId}`;
        }
        
        return `/item-${itemId}`;
      }
      
      return "#";
    },

    getPropertyValue(player, propertyId) {
      if (!player?.variationProperties) {
        return null;
      }
      
      for (let i = 0; i < player.variationProperties.length; i++) {
        const vprop = player.variationProperties[i];
        
        if (vprop.property && vprop.property.id === propertyId) {
          let value = null;
          
          if (vprop.values && Array.isArray(vprop.values) && vprop.values[0]) {
            value = vprop.values[0].value;
          }
          
          if (!value && vprop.values?.names?.value) {
            value = vprop.values.names.value;
          }
          
          if (!value && vprop.property?.values?.value) {
            value = vprop.property.values.value;
          }
          
          if (!value && vprop.property?.value) {
            value = vprop.property.value;
          }
          
          return value;
        }
        
        if (vprop.properties) {
          const result = vprop.properties.find((prop) => prop.id === propertyId);
          if (result?.values) {
            return result.values.value || null;
          }
        }
      }
      
      return null;
    },

    getTechnicalData(player) {
      if (!player) return null;
      
      if (player.texts && player.texts[0]) {
        const text = player.texts[0];
        
        if (text.technicalData) {
          return text.technicalData;
        }
        
        if (text.description2) {
          return text.description2;
        }
      }
      
      return null;
    },

    toggleTechnicalData(playerKey) {
      this.$set(this.technicalDataCollapsed, playerKey, !this.technicalDataCollapsed[playerKey]);
    },

    isMobile() {
      // SSR-safe: Return false on server, check window only on client
      if (typeof window === "undefined") {
        return false;
      }
      return window.innerWidth < 768;
    },

    renderPlayerCard(player, playerKey) {
      const h = this.$createElement;
      
      const playerName = playerKey === "player1" ? this.player1Name : 
                        playerKey === "player2" ? this.player2Name : 
                        this.player3Name;
      const playerPosition = playerKey === "player1" ? this.player1Position : 
                            playerKey === "player2" ? this.player2Position : 
                            this.player3Position;
      const playerMarketValue = playerKey === "player1" ? this.player1MarketValue : 
                               playerKey === "player2" ? this.player2MarketValue : 
                               this.player3MarketValue;
      const playerUrl = playerKey === "player1" ? this.player1Url : 
                       playerKey === "player2" ? this.player2Url : 
                       this.player3Url;
      const playerTechnicalData = playerKey === "player1" ? this.player1TechnicalData : 
                                 playerKey === "player2" ? this.player2TechnicalData : 
                                 this.player3TechnicalData;

      return h("div", { class: "col-12 col-md-4 mb-3" }, [
        h("div", { class: "player-card border border-radius p-3 text-center position-relative h-100" }, [
          h("h3", { class: "h2 mb-3" }, playerName),

          playerPosition
            ? h("p", { class: "mb-2" }, [
                h("i", { class: "fa fa-map-marker mr-2" }),
                "Position: " + playerPosition,
              ])
            : null,

          playerMarketValue
            ? h("p", { class: "font-weight-bold mb-3" }, "Marktwert: " + playerMarketValue)
            : null,

          h(
            "a",
            {
              class: "btn btn-outline-primary btn-sm mb-3 d-inline-flex align-items-center",
              attrs: {
                href: playerUrl,                
              },
            },
            [              
              "Zum Spieler",
              h("i", { class: "fa fa-arrow-right ml-2" }),
            ]
          ),

          playerTechnicalData
            ? h("div", { class: "technical-data mb-3" }, [
                h("h5", { 
                  class: "font-weight-bold h4 text-left mb-2 d-flex align-items-center justify-content-between",
                  style: "cursor: pointer;",
                  on: {
                    click: () => this.toggleTechnicalData(playerKey),
                  },
                }, [
                  "Leistungsdaten",
                  h("i", { 
                    class: this.technicalDataCollapsed[playerKey] ? "fa fa-chevron-down d-md-none" : "fa fa-chevron-up d-md-none",
                  }),
                ]),
                h("div", {   
                  class: this.technicalDataCollapsed[playerKey] ? "d-none d-md-block" : "",               
                  domProps: {
                    innerHTML: playerTechnicalData,
                  },
                }),
              ])
            : null,

          h("div", { class: "d-flex flex-row justify-content-center align-items-center", style: "gap: 1rem;" }, [
            h(
              "button",
              {
                class: this.getButtonClass(playerKey, "start"),
                attrs: {
                  disabled: this.isButtonDisabled(playerKey, "start"),
                },
                on: {
                  click: () => this.selectVote(playerKey, "start"),
                },
              },
              [
                this.selectedVotes.start === playerKey ? h("i", { class: "fa fa-check mr-2" }) : null,
                this.startText,
                this.votes[playerKey].start > 0 ? ` (${this.votes[playerKey].start})` : "",
              ]
            ),

            h(
              "button",
              {
                class: this.getButtonClass(playerKey, "bench"),
                attrs: {
                  disabled: this.isButtonDisabled(playerKey, "bench"),
                },
                on: {
                  click: () => this.selectVote(playerKey, "bench"),
                },
              },
              [
                this.selectedVotes.bench === playerKey ? h("i", { class: "fa fa-check mr-2" }) : null,
                this.benchText,
                this.votes[playerKey].bench > 0 ? ` (${this.votes[playerKey].bench})` : "",
              ]
            ),

            h(
              "button",
              {
                class: this.getButtonClass(playerKey, "sell"),
                attrs: {
                  disabled: this.isButtonDisabled(playerKey, "sell"),
                },
                on: {
                  click: () => this.selectVote(playerKey, "sell"),
                },
              },
              [
                this.selectedVotes.sell === playerKey ? h("i", { class: "fa fa-check mr-2" }) : null,
                this.sellText,
                this.votes[playerKey].sell > 0 ? ` (${this.votes[playerKey].sell})` : "",
              ]
            ),
          ]),
        ]),
      ]);
    },
  },

  render: function(h) {
    return h("div", { class: "start-bench-sell-widget" }, [
      this.message
        ? h("div", { 
            class: "alert alert-success text-center mb-2 py-2 px-3",
            style: { fontSize: "0.85rem" }
          }, this.message)
        : null,

      h("div", { class: "row" }, [
        this.renderPlayerCard(this.player1, "player1"),
        this.renderPlayerCard(this.player2, "player2"),
        this.renderPlayerCard(this.player3, "player3"),
      ]),

      // Zentraler Submit oder "Stimmen ändern" Button
      this.isLoggedIn
        ? h("div", { class: "text-center mt-4" }, [
            !this.hasSubmittedVote
              ? h(
                  "button",
                  {
                    class: "btn btn-primary btn-lg d-inline-flex align-items-center",
                    attrs: {
                      disabled: !this.canSubmit || this.isLoading,
                    },
                    on: {
                      click: this.submitVotes,
                    },
                  },
                  [                    
                    this.isLoading ? "Wird gesendet..." : "Stimme abgeben",
                    h("i", { class: "fa fa-check-circle ml-2" }),
                  ]
                )
              : h(
                  "button",
                  {
                    class: "btn btn-secondary btn-lg",
                    on: {
                      click: this.changeVotes,
                    },
                  },
                  [                    
                    "Stimmen ändern",
                    h("i", { class: "fa fa-refresh ml-2" }),
                  ]
                ),
            !this.hasSubmittedVote && !this.canSubmit
              ? h("p", { class: "mt-2" }, "Bitte wähle für jede Kategorie einen Spieler aus")
              : null,
          ])
        : null,

      !this.isLoggedIn
        ? h("div", { 
            class: "alert alert-info text-center mt-4 py-2 px-3",
            style: { fontSize: "0.85rem" }
          }, [
            h("i", { class: "fa fa-info-circle mr-2" }),
            "Bitte ",
            h(
              "a",
              {
                attrs: { href: "#" },
                on: { click: (e) => { e.preventDefault(); this.openLoginModal(); } },
              },
              "melde dich an"
            ),
            ", um abzustimmen.",
          ])
        : null,

      this.totalVotes.start + this.totalVotes.bench + this.totalVotes.sell > 0
        ? h("div", { class: "vote-summary mt-4 p-3 border rounded bg-light" }, [
            h("h5", { class: "text-center mb-3" }, "Spieler-Statistik"),
            h("div", { class: "row text-center" }, [
              h("div", { class: "col-4" }, [
                h("div", { class: "font-weight-bold mb-2" }, this.player1Name),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-success mr-1" }, `Start: ${this.votes.player1.start}`),
                ]),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-warning mr-1" }, `Bench: ${this.votes.player1.bench}`),
                ]),
                h("div", { class: "small" }, [
                  h("span", { class: "badge badge-danger mr-1" }, `Sell: ${this.votes.player1.sell}`),
                ]),
              ]),
              h("div", { class: "col-4" }, [
                h("div", { class: "font-weight-bold mb-2" }, this.player2Name),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-success mr-1" }, `Start: ${this.votes.player2.start}`),
                ]),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-warning mr-1" }, `Bench: ${this.votes.player2.bench}`),
                ]),
                h("div", { class: "small" }, [
                  h("span", { class: "badge badge-danger mr-1" }, `Sell: ${this.votes.player2.sell}`),
                ]),
              ]),
              h("div", { class: "col-4" }, [
                h("div", { class: "font-weight-bold mb-2" }, this.player3Name),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-success mr-1" }, `Start: ${this.votes.player3.start}`),
                ]),
                h("div", { class: "small mb-1" }, [
                  h("span", { class: "badge badge-warning mr-1" }, `Bench: ${this.votes.player3.bench}`),
                ]),
                h("div", { class: "small" }, [
                  h("span", { class: "badge badge-danger mr-1" }, `Sell: ${this.votes.player3.sell}`),
                ]),
              ]),
            ]),
          ])
        : null,
    ]);
  },
});
